class SoundManager {
  constructor() {
    this.ctx = null;
    this.enabled = true;
  }

  _c() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    }
    if (this.ctx.state === "suspended") this.ctx.resume();
    return this.ctx;
  }

  toggle() {
    this.enabled = !this.enabled;
    return this.enabled;
  }

  // ── Math practice sounds ──

  correct() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    const o = c.createOscillator(), g = c.createGain();
    o.connect(g); g.connect(c.destination);
    o.type = "square";
    o.frequency.setValueAtTime(523, t);
    o.frequency.setValueAtTime(784, t + 0.08);
    o.frequency.setValueAtTime(1047, t + 0.16);
    g.gain.setValueAtTime(0.12, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.25);
    o.start(t); o.stop(t + 0.25);
  }

  wrong() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    const o = c.createOscillator(), g = c.createGain();
    o.connect(g); g.connect(c.destination);
    o.type = "sawtooth";
    o.frequency.setValueAtTime(200, t);
    o.frequency.linearRampToValueAtTime(100, t + 0.25);
    g.gain.setValueAtTime(0.1, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.3);
    o.start(t); o.stop(t + 0.3);
  }

  timeout() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    for (let i = 0; i < 3; i++) {
      const o = c.createOscillator(), g = c.createGain();
      o.connect(g); g.connect(c.destination);
      o.type = "square";
      o.frequency.setValueAtTime(440, t + i * 0.12);
      g.gain.setValueAtTime(0.08, t + i * 0.12);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.12 + 0.1);
      o.start(t + i * 0.12); o.stop(t + i * 0.12 + 0.1);
    }
  }

  streak() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    const notes = [523, 659, 784, 1047];
    notes.forEach((f, i) => {
      const o = c.createOscillator(), g = c.createGain();
      o.connect(g); g.connect(c.destination);
      o.type = "square";
      o.frequency.setValueAtTime(f, t + i * 0.07);
      g.gain.setValueAtTime(0.1, t + i * 0.07);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.07 + 0.12);
      o.start(t + i * 0.07); o.stop(t + i * 0.07 + 0.12);
    });
  }

  reward() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    const notes = [523, 659, 784, 1047, 1319, 1568];
    notes.forEach((f, i) => {
      const o = c.createOscillator(), g = c.createGain();
      o.connect(g); g.connect(c.destination);
      o.type = "square";
      o.frequency.setValueAtTime(f, t + i * 0.1);
      g.gain.setValueAtTime(0.12, t + i * 0.1);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.1 + 0.2);
      o.start(t + i * 0.1); o.stop(t + i * 0.1 + 0.2);
    });
  }

  // ── Arcade game sounds ──

  shoot() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    const o = c.createOscillator(), g = c.createGain();
    o.connect(g); g.connect(c.destination);
    o.type = "square";
    o.frequency.setValueAtTime(880, t);
    o.frequency.exponentialRampToValueAtTime(220, t + 0.08);
    g.gain.setValueAtTime(0.08, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    o.start(t); o.stop(t + 0.08);
  }

  explosion() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    const buf = c.createBuffer(1, c.sampleRate * 0.15, c.sampleRate);
    const data = buf.getChannelData(0);
    for (let i = 0; i < data.length; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / data.length);
    const src = c.createBufferSource(), g = c.createGain();
    src.buffer = buf; src.connect(g); g.connect(c.destination);
    g.gain.setValueAtTime(0.15, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.15);
    src.start(t);
  }

  hit() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    const o = c.createOscillator(), g = c.createGain();
    o.connect(g); g.connect(c.destination);
    o.type = "triangle";
    o.frequency.setValueAtTime(300, t);
    o.frequency.exponentialRampToValueAtTime(60, t + 0.15);
    g.gain.setValueAtTime(0.12, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.2);
    o.start(t); o.stop(t + 0.2);
  }

  alienShoot() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    const o = c.createOscillator(), g = c.createGain();
    o.connect(g); g.connect(c.destination);
    o.type = "triangle";
    o.frequency.setValueAtTime(320, t);
    o.frequency.exponentialRampToValueAtTime(140, t + 0.18);
    g.gain.setValueAtTime(0.06, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    o.start(t); o.stop(t + 0.18);
  }

  gameOver() {
    if (!this.enabled) return;
    const c = this._c(), t = c.currentTime;
    const notes = [440, 392, 349, 330, 294, 262];
    notes.forEach((f, i) => {
      const o = c.createOscillator(), g = c.createGain();
      o.connect(g); g.connect(c.destination);
      o.type = "sawtooth";
      o.frequency.setValueAtTime(f, t + i * 0.15);
      g.gain.setValueAtTime(0.1, t + i * 0.15);
      g.gain.exponentialRampToValueAtTime(0.001, t + i * 0.15 + 0.2);
      o.start(t + i * 0.15); o.stop(t + i * 0.15 + 0.25);
    });
  }
}

const sounds = new SoundManager();
export default sounds;
